import { useEffect, useRef, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';

import {
  queryKeys,
  useEnsureLibraryTrackMutation,
  useLibraryImportQuery,
  type LibraryImport,
  type TrackCard,
} from '../../api/index.js';
import { useWorkspace } from '../../app/runtime.js';

export type TrackDownloadState = {
  readonly label: string;
  readonly disabled: boolean;
  readonly pending: boolean;
  readonly download: () => Promise<void>;
};

export function useTrackDownload(track: TrackCard): TrackDownloadState {
  const { library } = useWorkspace();
  const queryClient = useQueryClient();
  const mutation = useEnsureLibraryTrackMutation();
  const [acceptedImport, setAcceptedImport] = useState<LibraryImport>();
  const [importId, setImportId] = useState<string | undefined>(track.library_import?.id);

  useEffect(() => {
    const observedImportId = track.library_import?.id;
    if (observedImportId) setImportId((current) => current ?? observedImportId);
  }, [track.library_import?.id]);

  const importQuery = useLibraryImportQuery(library.id, importId);
  const observedImport = importQuery.data ?? acceptedImport ?? track.library_import;
  const state = trackDownloadPresentation(
    observedImport,
    mutation.isPending,
    Boolean(mutation.error),
  );
  const lifecycleRef = useRef(importLifecycleSignature(track.library_import));

  useEffect(() => {
    const lifecycle = importLifecycleSignature(observedImport);
    if (!lifecycle || lifecycle === lifecycleRef.current) return;
    lifecycleRef.current = lifecycle;
    void invalidateTrackDownloadReadModels(queryClient, track.id);
  }, [observedImport, queryClient, track.id]);

  const download = async () => {
    if (state.disabled) return;
    mutation.reset();
    try {
      const result = await mutation.mutateAsync({ libraryId: library.id, trackId: track.id });
      setAcceptedImport(result);
      setImportId(result.id);
      queryClient.setQueryData(queryKeys.import(library.id, result.id), result);
    } catch {
      // Mutation state keeps the same menu action retryable without adding another UI surface.
    }
  };

  return { ...state, download };
}

function importLifecycleSignature(managedImport: LibraryImport | null | undefined) {
  return managedImport
    ? `${managedImport.id}:${managedImport.status}:${managedImport.phase}`
    : null;
}

export function trackDownloadPresentation(
  managedImport: LibraryImport | null | undefined,
  commandPending: boolean,
  commandFailed: boolean,
): Omit<TrackDownloadState, 'download'> {
  if (commandPending) return { label: 'Downloading WAV…', disabled: true, pending: true };
  if (commandFailed) return { label: 'Download failed — retry', disabled: false, pending: false };
  if (!managedImport || managedImport.status === 'removed') {
    return { label: 'Download WAV', disabled: false, pending: false };
  }
  if (managedImport.status === 'pending' || managedImport.status === 'materializing') {
    return { label: 'Downloading WAV…', disabled: true, pending: true };
  }
  if (managedImport.status === 'available') {
    return { label: 'Downloaded WAV', disabled: true, pending: false };
  }
  return { label: 'Download failed', disabled: true, pending: false };
}

async function invalidateTrackDownloadReadModels(
  queryClient: QueryClient,
  trackId: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['catalog'] }),
    queryClient.invalidateQueries({ queryKey: ['project-cards'] }),
    queryClient.invalidateQueries({ queryKey: ['library-tracks'] }),
    queryClient.invalidateQueries({ queryKey: ['track', trackId] }),
    queryClient.invalidateQueries({ queryKey: ['activity'] }),
  ]);
}
